import { ButtonSecondarySmall } from '@peachjar/ui/dist/lib/components/Buttons';
import { cx } from '@emotion/css';
import { Translate, Card, MessageBanner, FlexInset } from '@/components';

import {
  BodyLargeEmphasized,
  BodyMedium,
  ButtonPrimaryMedium,
  ButtonFlatMedium,
  NavBarBackButton,
  TitleSmallEmphasized,
} from '@/design-system';
import { Add as AddIcon } from '@mui/icons-material';
import accountIcon from '@/assets/account-icon.svg';
import infoIcon from '@/assets/info-icon.svg';
import accountGreenIcon from '@/assets/account-green-icon.svg';
import accountInfoIcon from '@/assets/account-icon-pencil.svg';
import ChildrenEmptyStateIcon from '@/assets/children-empty-state-icon.svg';
import { ChildNewModel } from '@/hooks/useProfile';
import colors from '@/theme/colors';
import ChangeEmailForm from './components/ChangeEmailForm/ChangeEmailForm';
import ChildEnrollment from './components/ChildEnrollment/ChildEnrollment';
import {
  ChildGroupByCommunity,
  InstitutionFromSearch,
  GradeChangeInput,
  PendingSchools,
} from './AccountInformation.types';
import { bannersMessages, grades as gradeOptions } from './AccountInformation.constants';
import styles from './AccountInformation.styles';
import EmailNotificationsBanner from './components/EmailNotificationsBanner/EmailNotificationsBanner';
import { useReactiveVar } from '@apollo/client';
import { districtVar } from '@/graphql/state';
import { APP_ROUTES } from '@/config/constants';
import { useNavigate } from 'react-router-dom';

type AccountInformationProps = {
  userId: number | null;
  email: string;
  managedBy: string;
  childrenGrouped: ChildGroupByCommunity[];
  pendingSchools: PendingSchools[];
  childrenList: ChildNewModel[] | null;
  childrenAddingIds: Array<string>;
  dismissible: boolean;
  accountManagementEnabled: boolean;
  isNotificationEnabled: boolean;
  revertBlocked: boolean;
  updateRevertBlock: () => void;
  changeManagedByStatus: () => void;
  lookUpSchools: (options, callback) => void;
  handleSubmit: (childUId: string) => void;
  handleSchoolChange: (
    value: InstitutionFromSearch & { schoolId: number; districtId: number },
    id: number
  ) => void;
  handleGradeChange: (input: GradeChangeInput, childUId: string) => void;
  handleRemoveChild: (childUId: string) => void;
  handleRemoveAddedChild: (childUId: string) => void;
  handleAddChild: () => void;
  handleClosePage: () => void;
  handleCancel: (childUId: string) => void;
  handleDismissPendingNotification: (guardianId: number) => void;
};

export const AccountInformation: React.FC<AccountInformationProps> = ({
  userId,
  email,
  managedBy,
  childrenGrouped,
  pendingSchools,
  childrenList,
  childrenAddingIds,
  dismissible,
  accountManagementEnabled,
  isNotificationEnabled,
  revertBlocked,
  updateRevertBlock,
  changeManagedByStatus,
  lookUpSchools,
  handleSubmit,
  handleSchoolChange,
  handleGradeChange,
  handleRemoveChild,
  handleRemoveAddedChild,
  handleAddChild,
  handleClosePage,
  handleCancel,
  handleDismissPendingNotification,
}) => {
  const district = useReactiveVar(districtVar);
  const navigate = useNavigate();

  const onBackButtonClick = () => navigate(APP_ROUTES.account);

  const renderChildrenGrouped = () =>
    childrenGrouped.map(({ districts }, index) => (
      <>
        {districts.map(({ district, childrenIds }) => (
          <>
            <div className={styles.districtNameContainer}>
              <div className={styles.districtName}>
                <BodyLargeEmphasized data-testid="districtName" nonTranslate>
                  {district}
                </BodyLargeEmphasized>
              </div>
              <div>
                <BodyMedium>
                  Enter the school your child or children attend. Only schools that are using
                  Peachjar are available for selection.
                </BodyMedium>
              </div>
            </div>
            {childrenIds.map((childUid, index) => {
              const child = childrenList!.find(c => c.childUid === childUid);
              if (!child) return null;

              const { institutionId, institutionName, gradeLevel, grades, wasChanged } = child;

              const currentGrade = (grades || gradeOptions).find(g => g.description === gradeLevel);
              const pendingDistrict = pendingSchools.find(d => d.districtName === district);
              const gradeLevelChanged = pendingDistrict
                ? pendingDistrict.schools.some(
                    s => s.institutionName === institutionName && !s.isNewSchool
                  )
                : false;

              return (
                <ChildEnrollment
                  key={index}
                  index={index}
                  childUid={childUid}
                  disabled={!accountManagementEnabled}
                  grades={grades || gradeOptions}
                  wasAdded={false}
                  wasChanged={wasChanged}
                  institution={{ institutionId, institutionName }}
                  currentGrade={currentGrade}
                  lookUpSchools={lookUpSchools}
                  handleSubmit={handleSubmit}
                  handleSchoolChange={handleSchoolChange}
                  handleGradeChange={handleGradeChange}
                  handleRemoveChild={handleRemoveChild}
                  handleCancel={() => handleCancel(childUid)}
                  gradeLevelChanged={gradeLevelChanged}
                />
              );
            })}
          </>
        ))}
        {index < childrenGrouped.length - 1 && <div className={styles.separator} />}
      </>
    ));

  const renderAddedChildren = () =>
    childrenAddingIds.map((childUid, index) => {
      const { institutionId, institutionName, gradeLevel, grades, wasAdded, wasChanged } =
        childrenList!.find(c => c.childUid === childUid)!;
      const currentGrade = (grades || gradeOptions).find(g => g.description === gradeLevel);

      return (
        <ChildEnrollment
          index={index}
          childUid={childUid}
          disabled={false}
          grades={grades || gradeOptions}
          wasAdded={wasAdded}
          wasChanged={wasChanged}
          institution={{ institutionId, institutionName }}
          currentGrade={currentGrade}
          lookUpSchools={lookUpSchools}
          handleSubmit={handleSubmit}
          handleSchoolChange={handleSchoolChange}
          handleGradeChange={handleGradeChange}
          handleRemoveChild={handleRemoveAddedChild}
          handleCancel={handleRemoveAddedChild}
          gradeLevelChanged={false}
        />
      );
    });

  const renderButtonAdd = () =>
    accountManagementEnabled ? (
      // TODO
      <ButtonFlatMedium
        onClick={handleAddChild}
        data-testid="addChildButton"
        startAdornment={<AddIcon />}
      >
        Add Child
      </ButtonFlatMedium>
    ) : null;
  const InfoIcon = () => (
    <img src={infoIcon} className={styles.paddingInfoIcon} alt="Information Icon" />
  );

  const renderPendingSchools = () => (
    <>
      {pendingSchools.map(p => (
        <div className={styles.pendingSchoolsContainer}>
          <span className={styles.pendingDistrictName}>{p.districtName}</span>
          {p.schools.map(({ institutionName, gradeLevel, isNewSchool }) => (
            <div className={styles.pendingInfoContainer}>
              <div className={styles.alignPendingInfo}>
                {isNewSchool && <InfoIcon />}
                <span className={styles.pendingInfo}>{institutionName}</span>
              </div>
              <div className={styles.alignPendingInfo}>
                <InfoIcon />
                <span className={styles.pendingInfo}>
                  Grade {`${gradeLevel === 'N' || !gradeLevel ? 'Unknown' : gradeLevel}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );

  return (
    <>
      <NavBarBackButton trackId="account_information_page" onClickBackButton={onBackButtonClick}>
        Account Information
      </NavBarBackButton>
      <FlexInset>
        <div className={cx(styles.horizontalPadding, styles.headerSpacing)}>
          <TitleSmallEmphasized className={styles.heading}>Parent Account</TitleSmallEmphasized>
          <div className={styles.changeEmailContainer}>
            <ChangeEmailForm email={email} updateRevertBlock={updateRevertBlock} />
            <EmailNotificationsBanner isNotificationEnabled={isNotificationEnabled} />
          </div>
          {district?.isMultiDistrict && (
            <MessageBanner
              subheadline={bannersMessages.multiDistrict}
              iconSrc={infoIcon}
              variant="warning"
              dataTestId="multiDistrictAccount"
            />
          )}
        </div>
        <div className={styles.divider} />
        <div className={styles.horizontalPadding}>
          <div>
            <TitleSmallEmphasized>School Information</TitleSmallEmphasized>
            {managedBy === 'sis' && (
              <div className={styles.manageMyselfMessage}>
                <MessageBanner
                  subheadline={bannersMessages.manageByDistrict}
                  iconSrc={accountIcon}
                  variant="prussian"
                  dataTestId="manageByDistrictBanner"
                  footer={
                    <div className={styles.buttonManageMyself}>
                      <ButtonPrimaryMedium
                        onClick={changeManagedByStatus}
                        data-testid="manageMyselfButton"
                        fontColor={colors.jungle}
                        background={colors.jungle100}
                      >
                        Manage Myself
                      </ButtonPrimaryMedium>
                    </div>
                  }
                >
                  <BodyMedium color={colors.white}>
                    {bannersMessages.manageByDistrictDisclaimer}
                  </BodyMedium>
                </MessageBanner>
              </div>
            )}
            {managedBy === 'family' && (
              <MessageBanner
                subheadline={bannersMessages.manageByParent}
                iconSrc={accountGreenIcon}
                variant="pop"
                dataTestId="manageByParentBanner"
              />
            )}
            {!revertBlocked && managedBy === 'family' && (
              <MessageBanner
                iconSrc={infoIcon}
                subheadline={bannersMessages.revertManageBy}
                variant="warning"
                dataTestId="revertManageByBanner"
                footer={
                  <div className={styles.revertToDistrict} data-testid="revertManageByButton">
                    <ButtonFlatMedium onClick={changeManagedByStatus}>
                      Revert to district managed
                    </ButtonFlatMedium>
                  </div>
                }
              />
            )}
            {managedBy === 'family' && (
              <div className={styles.manageByParentDisclaimer}>
                <BodyMedium className={styles.description} data-testid="manageByParentDisclaimer">
                  Access flyers from your child’s school and school-approved community enrichment
                  resources.
                </BodyMedium>
              </div>
            )}
            {pendingSchools && pendingSchools.length > 0 && (
              <div className={styles.childNotUpToDateBanner}>
                <MessageBanner
                  subheadline={bannersMessages.childNotbeUpToDate}
                  iconSrc={accountInfoIcon}
                  dataTestId="childNotBeUpToDateBanner"
                  footer={
                    <div className={styles.dismissButton}>
                      <ButtonSecondarySmall
                        onClick={() => handleDismissPendingNotification(userId!)}
                        data-testid="dismissPendingNotificationButton"
                      >
                        <Translate>Dismiss</Translate>
                      </ButtonSecondarySmall>
                    </div>
                  }
                >
                  {renderPendingSchools()}
                  <BodyMedium color={colors.prussian}>
                    {bannersMessages.childNotbeUptoDateDisclaimer}
                  </BodyMedium>
                </MessageBanner>
              </div>
            )}
            {!childrenList ||
              (childrenList.length === 0 && (
                <div className={styles.emptyState} data-testid="emptyState">
                  <img src={ChildrenEmptyStateIcon} alt="Children empty state" />
                  <p>{bannersMessages.childrenEmptyState}</p>
                  {renderButtonAdd()}
                </div>
              ))}
            {renderChildrenGrouped()}
            {renderAddedChildren()}
          </div>
          {childrenList && childrenList.length > 0 && (
            <div className={styles.addSectionContainer}>{renderButtonAdd()}</div>
          )}
        </div>
      </FlexInset>
    </>
  );
};
